import * as Portal from "@radix-ui/react-portal";
import * as Sentry from "@sentry/react";
import { flexRender } from "@tanstack/react-table";
import { ArchiveRestore } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { RowsSelected } from "@/components/shared/tables/ActionBar";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUpdateManyLineItems } from "@/queries/line-items/update";

import { InvoiceReviewActionBar as ActionBar } from "../../InvoiceReviewActionBar";

import { LINE_ITEMS_QUERY } from "@/queries/line-items/fetch";
import { useState } from "react";
import { useLineItemTableInstance } from "./table-instance-provider";

export const ArchivedLineItemTable = () => {
  const { table, setRowSelection } = useLineItemTableInstance();
  const updateLineItems = useUpdateManyLineItems({
    refetchQueries: [LINE_ITEMS_QUERY],
  });
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const handleBulkUpdate = async () => {
    const lineItemIds = table
      .getSelectedRowModel()
      .rows.map((row) => row.original.lineItem.lineItemId);
    const toastId = toast.loading(t("generic_loading"));

    try {
      setLoading(true);
      await updateLineItems(lineItemIds, { deletedAt: null });
      setRowSelection([]);
      toast.dismiss(toastId);
    } catch (error) {
      Sentry.captureException(error);
      toast.error(t("generic_error"), { id: toastId, richColors: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full gap-2 flex flex-col">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: `${header.getSize()}px`,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      paddingLeft: header.id === "select" ? "5px" : "0px",
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => {
                  const hasMeta = cell.getContext().cell.column.columnDef.meta;
                  const style =
                    hasMeta?.getCellContext?.(cell.getContext())?.style ?? {};
                  return (
                    <TableCell
                      key={cell.id}
                      style={{
                        paddingRight: "0px",
                        paddingTop: "0px",
                        paddingBottom: "0px",
                        paddingLeft: cell.id.includes("select") ? "5px" : "0px",
                        ...style,
                      }}
                      className="truncate-short"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center"
              >
                Keine Buchungen
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {table.getSelectedRowModel().rows.length > 0 && (
        <Portal.Root
          container={document.getElementById("invoice-review-screen")}
        >
          <ActionBar stickyBottom={false} className="w-[340px] min-w-[340px]">
            <RowsSelected
              totalRowsLength={table?.getRowCount()}
              selectedRowsLength={table?.getSelectedRowModel()?.rows?.length}
              onDeselectAll={() => table.resetRowSelection(true)}
            />
            <Button
              type="submit"
              variant="outline"
              onClick={handleBulkUpdate}
              disabled={loading}
            >
              <ArchiveRestore className="mr-1" size={16} />
              {t("restore")}
            </Button>
          </ActionBar>
        </Portal.Root>
      )}
    </div>
  );
};
